import React, { useState, useEffect } from 'react';
import {
	PaymentElement,
	useStripe,
	useElements,
} from '@stripe/react-stripe-js';
import {
	Box,
	Button,
	VStack,
	HStack,
	Text,
	Alert,
	AlertIcon,
	AlertTitle,
	AlertDescription,
	Spinner,
	Divider,
	Radio,
	RadioGroup,
	Stack,
	Card,
	CardBody,
	Icon,
	Badge,
	Collapse,
	Input,
	useToast,
} from '@chakra-ui/react';
import {
	FaCreditCard,
	FaPlus,
	FaStar,
	FaCheckCircle,
	FaTimesCircle,
} from 'react-icons/fa';
import { useAuthenticatedUserInfo } from '../../../../services/Contexts/AuthenticatedUserInfoContext';
import { useQueryClient } from '@tanstack/react-query';
import { useStripePrice } from '../../../../hooks/useStripe';
import { isFeatureVisibleOnPlanTable } from '../../../../utils/featureUtils';

const EmbeddedCheckout = ({
	plan,
	onSuccess,
	onCancel,
	isYearly,
	isUpgrade = false,
	currentSubscription,
	plans,
	plansYear,
	showSuccessToast = true,
	deferSubscriptionRefresh = false,
	embeddedInModal = false,
	showMoneyBackGuarantee = false,
}) => {
	const stripe = useStripe();
	const elements = useElements();
	const { userId, userEmail, companyId, token } = useAuthenticatedUserInfo();
	const queryClient = useQueryClient();
	const toast = useToast();

	// Fetch prices for this plan
	const stripePriceId = isYearly
		? plan?.stripePriceIdYearly
		: plan?.stripePriceIdMonthly;
	const { data: stripePrice, isLoading: stripePriceLoading } =
		useStripePrice(stripePriceId);

	const [error, setError] = useState(null);
	const [processing, setProcessing] = useState(false);
	const [showPlanComparison, setShowPlanComparison] = useState(false);
	const [planChangeSummary, setPlanChangeSummary] = useState(null);
	const [planChangeSummaryLoading, setPlanChangeSummaryLoading] = useState(false);
	const [planChangeSummaryError, setPlanChangeSummaryError] = useState(null);
	const [promoCode, setPromoCode] = useState('');
	const [discount, setDiscount] = useState(null);
	const [checkoutLoading, setCheckoutLoading] = useState(false);
	const [showNewCard, setShowNewCard] = useState(false);
	const [paymentMethods, setPaymentMethods] = useState([]);
	const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('new');

	// Determine if it's an upgrade or downgrade
	const getChangeType = () => {
		if (!isUpgrade || !currentSubscription || !plan) return 'upgrade';

		// Get all available plans
		const allPlans = [...(plans || []), ...(plansYear || [])];

		// Find current plan
		const currentPlan = allPlans.find(
			(p) => p.id === currentSubscription.subscriptionPriceId,
		);

		if (!currentPlan) return 'change';

		// Check if it's the same plan but different billing cycle
		if (currentPlan.planName === plan.planName) {
			// Same plan, different billing cycle
			return 'billing-change';
		}

		// Use price comparison to determine upgrade/downgrade
		const currentBillingCycle = currentSubscription.billingCycle || 'monthly';
		const currentPrice = 0; // Price info comes from Stripe
		const newPrice = 0; // Price info comes from Stripe

		if (newPrice > currentPrice) return 'upgrade';
		if (newPrice < currentPrice) return 'downgrade';
		return 'change';

		if (newPrice > currentPrice) return 'upgrade';
		if (newPrice < currentPrice) return 'downgrade';
		return 'change';
	};

	const fetchPlanChangeSummary = async () => {
		if (!isUpgrade || !currentSubscription || !plan) return;

		const newPlanPriceId = isYearly
			? plan.stripePriceIdYearly
			: plan.stripePriceIdMonthly;

		if (!newPlanPriceId) return;

		setPlanChangeSummaryLoading(true);
		setPlanChangeSummaryError(null);

		try {
			const response = await fetch(
				`${process.env.REACT_APP_API_URL}/api/stripe/customer-portal/preview-plan-change`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({ newPlanPriceId }),
				},
			);

			const data = await response.json();

			if (!response.ok) {
				throw new Error(data.error || 'Failed to load plan pricing');
			}

			setPlanChangeSummary(data);
		} catch (err) {
			setPlanChangeSummary(null);
			setPlanChangeSummaryError(err.message || 'Could not load plan pricing');
		} finally {
			setPlanChangeSummaryLoading(false);
		}
	};

	// Apply promo code
	const applyPromoCode = async () => {
		if (!promoCode.trim()) return;

		try {
			setProcessing(true);
			const response = await fetch(
				`${process.env.REACT_APP_API_URL}/api/stripe/customer-portal/validate-coupon`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({
						couponCode: promoCode,
						planPriceId: isYearly
							? plan.stripePriceIdYearly
							: plan.stripePriceIdMonthly,
						companyId,
						userId,
					}),
				},
			);

			const data = await response.json();

			if (response.ok) {
				setDiscount(data.discount);
				toast({
					title: 'Promo code applied!',
					description: `You saved ${data.discount.amount_off ? `$${data.discount.amount_off}` : `${data.discount.percent_off}%`}`,
					status: 'success',
					duration: 3000,
				});
			} else {
				throw new Error(data.error || 'Invalid promo code');
			}
		} catch (error) {
			toast({
				title: 'Invalid promo code',
				description: error.message,
				status: 'error',
				duration: 3000,
			});
		} finally {
			setProcessing(false);
		}
	};

	// Get enhanced plan features comparison
	const getPlanFeatures = () => {
		if (!plan?.features || plan.features.length === 0) return [];

		return plan.features
			.filter(isFeatureVisibleOnPlanTable)
			.map((f) => {
				// Handle both cases: when feature is populated as object or when it's just an ID
				const featureName = f?.featureName || f?.name || 'Unknown Feature';
				const description = f?.description;

				// Get the limit for this plan from planLimits
				const planLimit = f?.planLimits?.find(
					(pl) => pl.plan === (plan._id || plan.id),
				);
				// Use displayLimit from backend (readableLimit || limit fallback)
				const limit =
					planLimit?.displayLimit || planLimit?.readableLimit || planLimit?.limit;

				return {
					featureName,
					limit: limit === null ? '✓' : limit,
					description,
				};
			});
	};

	// Format Stripe price data
	const formatStripePriceData = () => {
		if (!stripePrice) return null;

		let priceObj = null;

		// Check for the nested structure: stripePrice.data.data (API response structure)
		if (
			stripePrice.data &&
			stripePrice.data.data &&
			typeof stripePrice.data.data === 'object' &&
			stripePrice.data.data.unit_amount !== undefined
		) {
			priceObj = stripePrice.data.data;
		}
		// Check if stripePrice.data is the direct response
		else if (
			stripePrice.data &&
			typeof stripePrice.data === 'object' &&
			stripePrice.data.unit_amount !== undefined
		) {
			priceObj = stripePrice.data;
		}
		// Check if stripePrice is the direct price object
		else if (stripePrice.unit_amount !== undefined) {
			priceObj = stripePrice;
		} else {
			return null;
		}

		const unitAmount = priceObj.unit_amount;

		// Validate that unit_amount is a valid number
		if (unitAmount === null || unitAmount === undefined || isNaN(unitAmount)) {
			return null;
		}

		const amount = unitAmount / 100; // Convert from cents
		return {
			amount: amount.toLocaleString(),
			formatted: `$${amount.toLocaleString()}`,
		};
	};
	const getModalTitle = () => {
		if (!isUpgrade) {
			return `Subscribe to ${plan?.planName}`;
		}

		const changeType = getChangeType();

		if (changeType === 'billing-change') {
			const billingCycle = isYearly ? 'Annual' : 'Monthly';
			return `Change to ${plan?.planName} (${billingCycle})`;
		}

		const actionWord =
			changeType === 'upgrade'
				? 'Upgrade'
				: changeType === 'downgrade'
					? 'Downgrade'
					: 'Change';

		return `${actionWord} to ${plan?.planName}`;
	};

	// Get the appropriate button text based on change type
	const getButtonText = () => {
		if (!isUpgrade) {
			return 'Subscribe Now';
		}

		const changeType = getChangeType();

		if (changeType === 'billing-change') {
			const billingCycle = isYearly ? 'Annual' : 'Monthly';
			return `Switch to ${billingCycle}`;
		}

		const actionWord =
			changeType === 'upgrade'
				? 'Upgrade'
				: changeType === 'downgrade'
					? 'Downgrade'
					: 'Change';

		return `${actionWord} to ${plan?.planName}`;
	};

	// Fetch saved payment methods and plan-change pricing for upgrades
	useEffect(() => {
		if (isUpgrade && currentSubscription?.stripeCustomerId) {
			fetchPaymentMethods();
		}

		if (isUpgrade) {
			fetchPlanChangeSummary();
		} else {
			setPlanChangeSummary(null);
			setPlanChangeSummaryError(null);
		}
	}, [
		isUpgrade,
		currentSubscription?.stripeCustomerId,
		plan,
		isYearly,
	]);

	const fetchPaymentMethods = async () => {
		try {
			const response = await fetch(
				`${process.env.REACT_APP_API_URL}/api/stripe/customer-portal/payment-methods/${currentSubscription.stripeCustomerId}`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			);

			if (response.ok) {
				const data = await response.json();
				const methods = data.paymentMethods || [];
				setPaymentMethods(methods);

				// If there are saved payment methods, default to the first one
				if (methods.length > 0) {
					const defaultMethod = methods.find((pm) => pm.isDefault) || methods[0];
					setSelectedPaymentMethod(defaultMethod.id);
					setShowNewCard(false);
				}
			}
		} catch (error) {
			console.error('Error fetching payment methods:', error);
		}
	};

	const handleSubmit = async (event) => {
		event.preventDefault();

		if (!stripe || (!isUpgrade && !elements)) {
			return;
		}

		setProcessing(true);
		setCheckoutLoading(true);
		setError(null);

		try {
			let paymentMethodId;

			if (isUpgrade && selectedPaymentMethod !== 'new') {
				// Use existing payment method for upgrade
				paymentMethodId = selectedPaymentMethod;
			} else {
				// Create new payment method using PaymentElement
				if (!stripe || !elements) {
					throw new Error('Payment setup not ready');
				}

				// Use confirmSetup with PaymentElement - clientSecret is passed via Elements provider
				const { error: stripeError, setupIntent } = await stripe.confirmSetup({
					elements,
					confirmParams: {
						return_url: `${window.location.origin}/subscription-success`,
					},
					redirect: 'if_required',
				});

				if (stripeError) {
					throw new Error(stripeError.message);
				}

				paymentMethodId = setupIntent?.payment_method;

				if (!paymentMethodId) {
					throw new Error('No payment method created');
				}
			}

			// Choose the appropriate endpoint based on upgrade vs new subscription
			const endpoint = isUpgrade
				? '/api/stripe/customer-portal/update-subscription'
				: '/api/stripe/customer-portal/create-subscription';

			const requestBody = isUpgrade
				? {
						subscriptionId:
							currentSubscription?.subscriptionId ||
							currentSubscription?.paymentSubscriptionIntentId,
						newPlanPriceId: isYearly
							? plan.stripePriceIdYearly
							: plan.stripePriceIdMonthly,
						paymentMethodId,
						companyId,
						userId,
						userEmail,
					}
				: {
						paymentMethodId,
						planPriceId: isYearly
							? plan.stripePriceIdYearly
							: plan.stripePriceIdMonthly,
						planName: plan.planName,
						companyId,
						userId,
						userEmail,
					};

			// Create or update the subscription
			const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify(requestBody),
			});

			const subscriptionData = await response.json();

			if (!response.ok) {
				throw new Error(
					subscriptionData.error ||
						`Failed to ${isUpgrade ? 'update' : 'create'} subscription`,
				);
			}

			if (!deferSubscriptionRefresh) {
				queryClient.invalidateQueries(['subscription', companyId]);
				queryClient.invalidateQueries(['companies', companyId]);
			}

			// Show success message with animation
			if (showSuccessToast) {
				toast({
					title: isUpgrade
						? 'Plan Updated Successfully! 🎉'
						: 'Welcome to the team! 🎉',
					description: isUpgrade
						? `You've successfully upgraded to ${plan.planName}`
						: `You've successfully subscribed to ${plan.planName}. Check your email for confirmation.`,
					status: 'success',
					duration: 3000,
					isClosable: true,
				});
			}

			// Call success callback
			onSuccess(subscriptionData);
		} catch (err) {
			setError(err.message);
		} finally {
			setProcessing(false);
			setCheckoutLoading(false);
		}
	};

	const paymentElementOptions = {
		layout: 'tabs',
	};

	const cardElementOptions = {
		style: {
			base: {
				fontSize: '16px',
				color: '#424770',
				'::placeholder': {
					color: '#aab7c4',
				},
			},
			invalid: {
				color: '#df6060ff',
			},
		},
	};

	return (
		<Box
			maxW='100%'
			width='100%'
			mx='auto'
			p={embeddedInModal ? 0 : 6}
			bg={embeddedInModal ? 'transparent' : 'white'}
			position='relative'>
			<VStack spacing={embeddedInModal ? 5 : 6}>
				{!embeddedInModal && (
					<>
						<Text fontSize='xl' fontWeight='bold' textAlign='center' pb={0} mb={0}>
							{getModalTitle()}
						</Text>
						{showMoneyBackGuarantee ? (
							<Text fontSize='lg' fontWeight='600' textAlign='center' color='red.400'>
								30 Days Money Back Guaranteed <br />
								<span style={{ fontWeight: '300', fontSize: 'sm', color: 'gray.500' }}>
									When if you are not happy
								</span>
							</Text>
						) : null}
					</>
				)}

				<Box
					w='100%'
					p={4}
					bg={embeddedInModal ? 'gray.50' : 'gray.50'}
					borderRadius={embeddedInModal ? 'xl' : 'md'}
					border={embeddedInModal ? '1px solid' : undefined}
					borderColor={embeddedInModal ? 'gray.100' : undefined}>
					<VStack spacing={3} align='stretch'>
						{!embeddedInModal ? (
							<HStack justify='space-between' align='start'>
								<VStack align='start' spacing={1}>
									<Text fontWeight='bold' fontSize='lg'>
										{plan?.planName}
									</Text>
									<HStack>
										{stripePriceLoading ? (
											<Text fontSize='sm' color='gray.500'>
												Loading price...
											</Text>
										) : formatStripePriceData() ? (
											<>
												<Text fontSize='2xl' fontWeight='bold' color='blue.500'>
													{formatStripePriceData().formatted}
												</Text>
												<Text fontSize='sm' color='gray.600'>
													/{isYearly ? 'year' : 'month'}
												</Text>
											</>
										) : (
											<Text fontSize='sm' color='gray.600'>
												Contact sales for pricing
											</Text>
										)}
									</HStack>
									{plan?.description && (
										<Text fontSize='sm' color='gray.600'>
											{plan.description}
										</Text>
									)}
								</VStack>

								<Button
									size='sm'
									variant='solid'
									colorScheme='gray'
									onClick={() => setShowPlanComparison(!showPlanComparison)}>
									{showPlanComparison ? 'Hide' : 'View'} Features
								</Button>
							</HStack>
						) : (
							<HStack justify='space-between' align='center' flexWrap='wrap' gap={2}>
								<Box>
									{plan?.description && (
										<Text fontSize='sm' color='gray.600' m={0}>
											{plan.description}
										</Text>
									)}
								</Box>
								<Button
									size='sm'
									variant='outline'
									colorScheme='teal'
									onClick={() => setShowPlanComparison(!showPlanComparison)}>
									{showPlanComparison ? 'Hide features' : 'View plan features'}
								</Button>
							</HStack>
						)}
						{/* Plan change: full price today, new billing period */}
						{isUpgrade && (
							<Box
								p={3}
								bg='blue.50'
								borderRadius='md'
								border='1px'
								borderColor='blue.200'>
								<Text fontSize='sm' fontWeight='medium' color='blue.800' mb={2}>
									What you&apos;ll pay
								</Text>
								{planChangeSummaryLoading ? (
									<HStack spacing={2} py={2}>
										<Spinner size='sm' color='blue.500' />
										<Text fontSize='sm' color='blue.700' m={0}>
											Loading plan price…
										</Text>
									</HStack>
								) : planChangeSummaryError ? (
									<Alert status='warning' borderRadius='md' fontSize='sm'>
										<AlertIcon />
										<AlertDescription m={0}>{planChangeSummaryError}</AlertDescription>
									</Alert>
								) : planChangeSummary ? (
									<VStack spacing={2} align='stretch'>
										<HStack justify='space-between'>
											<Text fontSize='sm' color='blue.700' m={0}>
												Due today (full plan price):
											</Text>
											<Text fontSize='lg' fontWeight='bold' color='blue.600' m={0}>
												${planChangeSummary.totalDue}
											</Text>
										</HStack>
										{planChangeSummary.nextBillingDate && (
											<Text fontSize='xs' color='blue.600' m={0}>
												Next billing: {planChangeSummary.nextBillingDate}
											</Text>
										)}
										<Text fontSize='xs' color='gray.600' m={0}>
											Your new billing period starts today. Unused time on your
											previous plan is not credited when you switch.
										</Text>
									</VStack>
								) : null}
							</Box>
						)}

						{/* Plan Features Comparison */}
						<Collapse in={showPlanComparison} animateOpacity>
							<Box>
								<Text fontSize='sm' fontWeight='medium' color='gray.700' mb={2}>
									Plan Features:
								</Text>
								<VStack spacing={1} align='stretch'>
									{getPlanFeatures().map((feature, index) => (
										<HStack key={index} spacing={2}>
											<Icon as={FaCheckCircle} color='green.500' boxSize={4} />
											<Text fontSize='sm' color='gray.700'>
												{feature.featureName}
												{feature.limit && (
													<Text as='span' color='blue.600' ml={1}>
														({feature.limit})
													</Text>
												)}
											</Text>
										</HStack>
									))}
								</VStack>
							</Box>
						</Collapse>

						{/* Promo Code Section */}
						<Box>
							<Text fontSize='sm' fontWeight='medium' color='gray.700' mb={2}>
								Promo Code (Optional):
							</Text>
							<HStack>
								<Input
									size='sm'
									placeholder='Enter promo code'
									value={promoCode}
									onChange={(e) => setPromoCode(e.target.value)}
									isDisabled={discount !== null}
								/>
								<Button
									size='sm'
									colorScheme='teal'
									variant='outline'
									onClick={applyPromoCode}
									isLoading={processing}
									isDisabled={!promoCode.trim() || discount !== null}>
									Apply
								</Button>
							</HStack>
							{discount && (
								<Text fontSize='sm' color='green.600' mt={1}>
									✓ Code applied:{' '}
									{discount.amount_off
										? `$${discount.amount_off} off`
										: `${discount.percent_off}% off`}
								</Text>
							)}
						</Box>
					</VStack>
				</Box>

				{!embeddedInModal && <Divider />}

				{error && (
					<Alert status='error' borderRadius='md'>
						<AlertIcon />
						<Box>
							<AlertTitle>Payment Error</AlertTitle>
							<AlertDescription>{error}</AlertDescription>
						</Box>
					</Alert>
				)}

				{/* Loading Overlay */}
				{checkoutLoading && (
					<Box
						position='absolute'
						top={0}
						left={0}
						right={0}
						bottom={0}
						bg='whiteAlpha.900'
						display='flex'
						alignItems='center'
						justifyContent='center'
						zIndex={10}
						borderRadius='md'>
						<VStack spacing={4}>
							<Spinner size='lg' color='blue.500' />
							<Text fontSize='lg' fontWeight='medium'>
								{processing ? 'Processing payment...' : 'Setting up checkout...'}
							</Text>
							<Text fontSize='sm' color='gray.600' textAlign='center'>
								Please do not close this window
							</Text>
						</VStack>
					</Box>
				)}

				<form
					onSubmit={handleSubmit}
					style={{ width: '100%', position: 'relative' }}>
					<VStack spacing={4}>
						{/* Payment Method Selection for Upgrades */}
						{isUpgrade && paymentMethods.length > 0 && (
							<Box w='100%'>
								<Text fontSize='sm' color='gray.600' mb={3}>
									Choose Payment Method:
								</Text>
								<RadioGroup
									value={selectedPaymentMethod}
									onChange={(value) => {
										setSelectedPaymentMethod(value);
										setShowNewCard(value === 'new');
									}}>
									<Stack spacing={3}>
										{paymentMethods.map((pm) => (
											<Card key={pm.id} size='sm'>
												<CardBody>
													<Radio value={pm.id}>
														<HStack spacing={3}>
															<Icon as={FaCreditCard} color='gray.500' />
															<VStack align='start' spacing={0}>
																<Text fontSize='sm' fontWeight='medium'>
																	**** **** **** {pm.card?.last4 || pm.last4 || '****'}
																</Text>
																<HStack spacing={2}>
																	<Text fontSize='xs' color='gray.500'>
																		{(pm.card?.brand || pm.brand || 'Card')?.toUpperCase()} •{' '}
																		{pm.card?.exp_month || pm.exp_month}/
																		{pm.card?.exp_year || pm.exp_year}
																	</Text>
																	{pm.isDefault && (
																		<Badge colorScheme='teal' size='sm'>
																			<Icon as={FaStar} mr={1} />
																			Default
																		</Badge>
																	)}
																</HStack>
															</VStack>
														</HStack>
													</Radio>
												</CardBody>
											</Card>
										))}

										<Card size='sm'>
											<CardBody>
												<Radio value='new'>
													<HStack spacing={3}>
														<Icon as={FaPlus} color='blue.500' />
														<Text fontSize='sm' fontWeight='medium' mb={0}>
															Use a new card
														</Text>
													</HStack>
												</Radio>
											</CardBody>
										</Card>
									</Stack>
								</RadioGroup>
							</Box>
						)}

						{/* Payment Element (replaces CardElement for better UX) */}
						<Collapse
							in={showNewCard || !isUpgrade}
							animateOpacity
							style={{ width: '100%' }}>
							<Box
								width='100%'
								p={4}
								border='1px'
								borderColor='gray.200'
								borderRadius={embeddedInModal ? 'xl' : 'md'}
								bg='white'>
								<PaymentElement options={paymentElementOptions} />
							</Box>
						</Collapse>

						<Box w='100%' display='flex' gap={3} flexDirection={{ base: 'column', sm: 'row' }}>
							<Button
								variant='outline'
								onClick={onCancel}
								isDisabled={processing}
								py={embeddedInModal ? 6 : 8}
								fontSize='md'
								flex={1}
								borderRadius='lg'>
								Cancel
							</Button>
							<Button
								type='submit'
								colorScheme={embeddedInModal && isUpgrade ? 'purple' : 'teal'}
								isLoading={processing}
								loadingText={isUpgrade ? 'Updating plan...' : 'Processing...'}
								isDisabled={!stripe || !elements}
								py={embeddedInModal ? 6 : 8}
								fontSize='md'
								flex={1}
								borderRadius='lg'
								boxShadow={embeddedInModal ? 'md' : undefined}
								_hover={embeddedInModal ? { transform: 'translateY(-1px)' } : undefined}>
								<VStack spacing={0}>
									<Text m={0}>{getButtonText()}</Text>
									{planChangeSummary && (
										<Text fontSize='xs' opacity={0.8}>
											(${planChangeSummary.totalDue} today)
										</Text>
									)}
									{discount && (
										<Text fontSize='xs' opacity={0.8}>
											(
											{discount.amount_off
												? `$${discount.amount_off}`
												: `${discount.percent_off}%`}{' '}
											saved!)
										</Text>
									)}
								</VStack>
							</Button>
						</Box>
					</VStack>
				</form>

				{!embeddedInModal && (
					<Text fontSize='xs' color='gray.500' textAlign='center'>
						Your payment information is secure and encrypted. You can cancel anytime.
					</Text>
				)}
			</VStack>
		</Box>
	);
};

export default EmbeddedCheckout;
